import 'moment-timezone';

import { InjectionToken } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Interval } from '@models';
import {
    MapperDefinition,
    MapperExplicit,
    ODataComparisonProperty,
    ODataFilterProperty,
    ODataQueryOptions,
} from '@services';
import {
    IFilterModel,
    ISortModel,
} from '@uipath/angular/components/ui-grid';

import * as moment from 'moment';

interface ICollectionMap {
    collectionPath: string | null;
    property: string | null;
}

const TIME_FILTER_PROPERTIES = ['creationTime', 'createdOn', 'time', 'timestamp'];

const intervalToDate = (interval: Interval) => {
    const now = moment();

    switch (interval) {
        case Interval.Hour:
            return now.clone().subtract(60, 'minute').toDate();
        case Interval.Day:
            return now.clone().subtract(1, 'day').minute(0).toDate();
        case Interval.Week:
            return now.clone().subtract(7, 'day').minute(0).toDate();
        case Interval.Month:
            return now.clone().subtract(1, 'month').minute(0).toDate();
    }
};

export const FILTER_BUILDER = new InjectionToken<ODataFilterBuilder<{}>>('Odata-Filter-Builder');

export class ODataFilterBuilder<T> {
    public build(
        page: PageEvent,
        sort: ISortModel<T>,
        searchFilters: IFilterModel<T>[],
        filters: IFilterModel<T>[],
        expand: string[],
        select: string[],
        mapper: MapperDefinition<T>,
    ): ODataQueryOptions {
        const queryOptions: ODataQueryOptions = {
            skip: page.pageIndex * page.pageSize,
            top: page.pageSize,
        };

        if (!!sort.direction) {
            const sortKey = this._getMappedKey(sort.field, mapper);
            queryOptions.orderBy = [`${sortKey} ${sort.direction}`];
        }

        if (expand.length > 0) {
            queryOptions.expand = expand;
        }

        if (select.length > 0) {
            queryOptions.select = select;
        }

        const filter = {
            or: [],
            and: [],
        } as ODataComparisonProperty;

        if (searchFilters) {
            filter.or = searchFilters.map(searchFilter => {
                const property: any = {};
                const mappedValue = this._getMappedKey(searchFilter.property, mapper);
                if (typeof mappedValue === 'string') {
                    property[mappedValue] = {};
                    const method = (searchFilter.method || 'Contains').toLowerCase();
                    property[mappedValue][method] = searchFilter.value;
                    return property;
                }
                throw new Error('Collection filtering not implemented');
            });
        }

        const timeFilters = filters
            .filter(f => TIME_FILTER_PROPERTIES.includes(f.property as string))
            .map(f => ({
                ...f,
                value: intervalToDate(f.value as Interval),
            } as IFilterModel<T>));
        const stdFilters = filters.filter(f => !TIME_FILTER_PROPERTIES.includes(f.property as string));

        filter.and!.push(...this._getFilterDefinitions([...timeFilters, ...stdFilters], mapper));

        queryOptions.filter = filter;

        return queryOptions;
    }

    protected _getMappedKey(
        key: keyof T | string,
        mapper: MapperDefinition<T>,
    ): string | ICollectionMap | null {
        if (typeof key === 'symbol') {
            return null;
        }

        const mappedValue = key as string;

        if (mapper == null) {
            return mappedValue;
        }

        const parts = mappedValue.split('.');

        let currentMapping = mapper;

        const collectionMap: ICollectionMap = {
            property: null,
            collectionPath: null,
        };

        const collectionPath = <string[]>[];
        let isParentAnArray = false;

        for (let i = 0; i < parts.length; i += 1) {
            const part = parts[i] as keyof T;
            const currentMapper: any = (currentMapping[part] || {}) as MapperExplicit<T>;
            const isCurrentPathAnArray = currentMapper.isCollection;

            const mapValue = currentMapper.property || currentMapping[part];
            if (isParentAnArray) {
                collectionMap.property = mapValue;
            } else if (mapValue instanceof Array) {
                currentMapping = mapValue[1];
                collectionPath.push(mapValue[0]);
            } else {
                collectionPath.push(mapValue);
            }

            isParentAnArray = isCurrentPathAnArray;
        }

        if (collectionMap.property != null) {
            collectionMap.collectionPath = collectionPath.join('/');
            return collectionMap;
        }

        return collectionPath.join('/');
    }

    private _getFilterDefinitions(filterModel: IFilterModel<T>[], mapper: MapperDefinition<T>): ODataFilterProperty[] {
        const filterDefinitions: ODataFilterProperty[] = [];
        filterModel.forEach(filter => {
            const filterValue = filter.value instanceof Array ? filter.value : [filter];

            filterDefinitions.push(
                ...filterValue.map(subFilter =>
                    this._generateFilterEntry(subFilter, filter.property, mapper),
                ),
            );
        });

        return filterDefinitions;
    }

    private _generateFilterEntry(
        filter: IFilterModel<T>,
        key: keyof T | string,
        mapper: MapperDefinition<T>,
    ): ODataFilterProperty {
        const filterDefinition = {} as ODataFilterProperty;

        const value = filter.type == null ?
            filter.value :
            {
                type: filter.type,
                value: filter.value,
            };

        const mappedKey = this._getMappedKey(key, mapper);

        if (mappedKey) {
            const propertyKey = this._getMappedKey(filter.property || key, mapper);
            if (typeof propertyKey === 'string') {
                filterDefinition[propertyKey] = {
                    [(filter.method || 'eq').toLowerCase()]: value,
                };
            } else if (propertyKey) {
                filterDefinition[propertyKey.collectionPath!] = {
                    any: {
                        [propertyKey.property!]: {
                            [(filter.method || 'eq').toLowerCase()]: value,
                        },
                    },
                };
            }
        }

        return filterDefinition;
    }
}
